class CartCrossSell extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('change', (event) => {
      const select = event.target.closest('[data-cross-sell-variant]');
      if (!select) return;
      const option = select.selectedOptions[0];
      const form = select.form;
      form.querySelector('[type="submit"]').disabled = !select.value || option.disabled;
      form.querySelector('[data-cross-sell-error]').hidden = true;
      if (!select.value || option.disabled) return;
      const price = this.querySelectorForCard(select, '[data-cross-sell-price]');
      const current = document.createElement('span');
      current.className = option.dataset.compare ? 'text-on-sale' : 'text-subdued';
      current.textContent = option.dataset.price;
      price.replaceChildren(current);
      if (option.dataset.compare) {
        const previous = document.createElement('s');
        previous.className = 'text-subdued';
        previous.textContent = option.dataset.compare;
        price.append(document.createTextNode(' '), previous);
      }
    }, { signal });
    this.addEventListener('cart:error', (event) => {
      const error = event.target.querySelector('[data-cross-sell-error]');
      if (error) {
        error.textContent = event.detail.error;
        error.hidden = false;
      }
    }, { signal });
    this.addEventListener('submit', (event) => {
      const form = event.target.closest('.cart-cross-sell-form');
      if (!form) return;
      event.preventDefault();
      this.addToCart(form);
    }, { signal });
    this.loading = this.load(signal);
  }

  async addToCart(form) {
    if (form.dataset.submitting || !form.reportValidity()) return;
    const button = form.querySelector('[type="submit"]');
    const select = form.querySelector('select');
    const error = form.querySelector('[data-cross-sell-error]');
    const status = document.getElementById('cart-cross-sell-status');
    const formData = new FormData(form);
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true, detail: { sections }
    }));
    formData.set('sections', sections.join(','));
    formData.set('sections_url', `${window.Shopify.routes.root}variants/${formData.get('id')}`);
    form.dataset.submitting = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (select) select.disabled = true;
    error.hidden = true;
    if (status) status.textContent = '';
    let added = false;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST', body: formData, headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || error.dataset.fallback);
      added = true;
      if (status) status.textContent = status.dataset.addedMessage;
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!cartResponse.ok) throw new Error();
      const cart = await cartResponse.json();
      cart.sections = result.sections;
      form.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: result.items || [result], cart, onSuccessDo: 'force_open_drawer' }
      }));
      if (!result.sections || sections.some(id => !result.sections[id])) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
        }));
      }
    } catch (failure) {
      if (added) {
        // Don't invite another add when only the subsequent cart refresh failed.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else {
        error.textContent = failure.message && failure.name !== 'TypeError' ? failure.message : error.dataset.fallback;
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
      if (!added) {
        delete form.dataset.submitting;
        if (select) select.disabled = false;
        button.disabled = select ? !select.value : false;
      }
    }
  }

  querySelectorForCard(element, selector) {
    return element.closest('[data-cross-sell-card]').querySelector(selector);
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  async load(signal) {
    const list = this.querySelector('[data-cross-sell-list]');
    this.hidden = true;
    list.replaceChildren();
    try {
      const cart = JSON.parse(this.dataset.cart);
      const excluded = new Set(cart.map((item) => String(item.id)));
      // One request per distinct cart product, preserving cart and editorial order.
      // Shopify returns up to ten complementary products for each source.
      for (const sourceId of new Set(cart.map(item => String(item.id)))) {
        if (signal.aborted) return;
        try {
          const url = new URL(`${window.Shopify.routes.root}recommendations/products`, window.location.origin);
          url.search = new URLSearchParams({
            section_id: this.dataset.sectionId,
            product_id: sourceId,
            intent: 'complementary',
            limit: '10'
          });
          const response = await fetch(url, { signal });
          if (!response.ok) continue;
          const html = await response.text();
          if (signal.aborted || !this.isConnected) return;
          const candidates = new DOMParser().parseFromString(html, 'text/html')
            .querySelectorAll('template[data-cross-sell-candidate]');
          for (const candidate of candidates) {
            const { productId } = candidate.dataset;
            if (excluded.has(productId)) continue;
            excluded.add(productId);
            list.append(candidate.content.cloneNode(true));
          }
          this.hidden = list.childElementCount === 0;
        } catch {
          if (signal.aborted) return;
          // A failed source must not suppress recommendations from other products.
        }
      }
      this.hidden = list.childElementCount === 0;
    } catch {
      // Recommendations must never block the existing cart or checkout.
    }
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
